import { useEffect, useState } from 'react'
import { client } from '../lib/api'
import { CONNECTION_INTENT_LABEL, outreachLabel, placeholderGradient } from '../lib/ui'
import { ReportBlock } from './ReportBlock'
import { SocialLinksRow } from './SocialLinks'
import type { PublicProfile } from '../../shared/types'
import type { SocialLink } from '../../shared/social-links'

export type ProfileSheetProps = {
  profileId: string | null
  onClose: () => void
  notify: (msg: string) => void
  onLike?: (profile: PublicProfile) => void
  onUnlike?: (profileId: string) => void
  onMessage?: (profile: PublicProfile) => void
  onPass?: (profileId: string) => void
  showActions?: boolean
  /** Full-screen Tinder/Seeking-style viewer vs bottom drawer */
  variant?: 'sheet' | 'fullscreen'
  /** Viewer already liked this person (Favorites → Your likes) */
  likedByViewer?: boolean
  /** Mutual match / they liked back */
  likedBack?: boolean
}

export function ProfileSheet({
  profileId,
  onClose,
  notify,
  onLike,
  onUnlike,
  onMessage,
  onPass,
  showActions = true,
  variant = 'fullscreen',
  likedByViewer = false,
  likedBack = false,
}: ProfileSheetProps) {
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<{
    profile: Record<string, unknown>
    tags: string[]
    socialLinks: SocialLink[]
    photos: Array<{ id: string; urlPath: string | null; kind: string }>
  } | null>(null)
  const [photoIdx, setPhotoIdx] = useState(0)

  useEffect(() => {
    if (!profileId) {
      setDetail(null)
      return
    }
    setLoading(true)
    setPhotoIdx(0)
    void client.recordProfileView(profileId).catch(() => undefined)
    void client
      .profileDetail(profileId)
      .then((d) => setDetail(d))
      .catch((e) => {
        notify((e as Error).message)
        onClose()
      })
      .finally(() => setLoading(false))
  }, [profileId, notify, onClose])

  if (!profileId) return null

  const p = detail?.profile
  const photos = detail?.photos.filter((ph) => ph.urlPath && ph.kind === 'public') ?? []
  const displayName = String(p?.display_name ?? 'Member')
  const accent = String(p?.accent_color ?? '#8ed9b7')
  const birth = p?.birth_year as number | null
  const age = birth ? new Date().getFullYear() - birth : null
  const publicProfile: PublicProfile | null = p
    ? {
        id: profileId,
        displayName,
        age,
        city: (p.city as string) ?? null,
        distanceMiles: null,
        outreachPreference: (p.outreach_preference as PublicProfile['outreachPreference']) ?? 'either',
        connectionIntent: (p.connection_intent as PublicProfile['connectionIntent']) ?? 'exploring',
        lifestyleTags: detail?.tags ?? [],
        heading: (p.heading as string) ?? null,
        bio: (p.bio as string) ?? null,
        accentColor: accent,
        verified: p.verification_status === 'verified',
        online: false,
        membershipBadge: p.verification_status === 'verified' ? 'Verified' : null,
        socialLinks: detail?.socialLinks,
      }
    : null

  const prevPhoto = () => setPhotoIdx((i) => Math.max(0, i - 1))
  const nextPhoto = () => setPhotoIdx((i) => Math.min(photos.length - 1, i + 1))

  const actionBar =
    showActions && publicProfile ? (
      <div className={variant === 'fullscreen' ? 'profile-viewer-actions' : 'profile-sheet-actions'}>
        {onPass && (
          <button type="button" className="circle-action skip" aria-label="Pass" onClick={() => onPass(profileId)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        )}
        {onUnlike && likedByViewer && (
          <button
            type="button"
            className="circle-action skip"
            aria-label="Remove like"
            onClick={() => onUnlike(profileId)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" fill="none" />
              <path d="m5 5 14 14" />
            </svg>
          </button>
        )}
        {onMessage && (
          <button type="button" className="message-action" onClick={() => onMessage(publicProfile)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
            </svg>
            Message
          </button>
        )}
        {onLike && (
          <button
            type="button"
            className="circle-action like"
            aria-label={likedByViewer ? 'Already liked' : likedBack ? 'Already matched' : 'Like'}
            disabled={likedBack || likedByViewer}
            onClick={() => onLike(publicProfile)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
            </svg>
          </button>
        )}
      </div>
    ) : null

  if (variant === 'fullscreen') {
    return (
      <div className="profile-viewer" role="dialog" aria-label={`${displayName} profile`}>
        <div className="profile-viewer-backdrop" onClick={onClose} />
        <div className="profile-viewer-panel">
          <header className="profile-viewer-top">
            {photos.length > 1 && (
              <div className="profile-viewer-progress">
                {photos.map((_, i) => (
                  <span key={i} className={i === photoIdx ? 'active' : i < photoIdx ? 'done' : ''} />
                ))}
              </div>
            )}
            <button type="button" className="profile-viewer-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          {loading && (
            <div className="loading-center profile-viewer-loading">
              <div className="spinner" />
            </div>
          )}

          {!loading && p && publicProfile && (
            <>
              <div className="profile-viewer-photos">
                {photos.length > 0 ? (
                  <>
                    <img src={photos[photoIdx]?.urlPath ?? ''} alt="" className="profile-viewer-photo" />
                    <button type="button" className="profile-viewer-tap left" aria-label="Previous photo" onClick={prevPhoto} />
                    <button type="button" className="profile-viewer-tap right" aria-label="Next photo" onClick={nextPhoto} />
                  </>
                ) : (
                  <div className="profile-viewer-photo-fallback" style={{ background: placeholderGradient(accent) }}>
                    {displayName.slice(0, 1)}
                  </div>
                )}
                <div className="profile-viewer-photo-shade" />
                <div className="profile-viewer-photo-meta">
                  {publicProfile.verified && <span className="verified">✓ Verified</span>}
                  <h2>
                    {displayName}
                    {age != null ? `, ${age}` : ''}
                  </h2>
                  <p className="profile-viewer-sub">
                    {outreachLabel(publicProfile.outreachPreference)} ·{' '}
                    {CONNECTION_INTENT_LABEL[publicProfile.connectionIntent]}
                    {publicProfile.city ? ` · ${publicProfile.city}` : ''}
                  </p>
                </div>
              </div>

              <div className="profile-viewer-body">
                {(p.heading as string) && <p className="profile-sheet-heading">{String(p.heading)}</p>}
                {(p.bio as string) && <p className="profile-sheet-bio">{String(p.bio)}</p>}
                <SocialLinksRow links={detail?.socialLinks ?? []} />
                {detail?.tags.length ? (
                  <div className="chip-row">
                    {detail.tags.map((tag) => (
                      <span key={tag} className="chip selected">
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
                <ReportBlock reportedId={profileId} targetType="user" targetId={profileId} notify={notify} />
              </div>

              {actionBar}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer profile-sheet" role="dialog" aria-label={`${displayName} profile`}>
        <div className="drawer-head">
          <h2>{displayName}</h2>
          <button type="button" className="button secondary" onClick={onClose}>
            Close
          </button>
        </div>

        {loading && (
          <div className="loading-center">
            <div className="spinner" />
          </div>
        )}

        {!loading && p && (
          <>
            <div className="profile-sheet-hero">
              {photos.length > 0 ? (
                <>
                  <img src={photos[photoIdx]?.urlPath ?? ''} alt="" className="profile-sheet-photo" />
                  {photos.length > 1 && (
                    <div className="profile-sheet-photo-nav">
                      <button type="button" className="button secondary" disabled={photoIdx === 0} onClick={prevPhoto}>
                        ‹
                      </button>
                      <span>
                        {photoIdx + 1} / {photos.length}
                      </span>
                      <button
                        type="button"
                        className="button secondary"
                        disabled={photoIdx >= photos.length - 1}
                        onClick={nextPhoto}
                      >
                        ›
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="profile-sheet-photo-fallback" style={{ background: placeholderGradient(accent) }}>
                  {displayName.slice(0, 1)}
                </div>
              )}
            </div>

            <div className="profile-sheet-meta">
              <div>
                <strong>
                  {displayName}
                  {age != null ? `, ${age}` : ''}
                </strong>
                <small>
                  {outreachLabel(publicProfile!.outreachPreference)} ·{' '}
                  {CONNECTION_INTENT_LABEL[publicProfile!.connectionIntent]}
                  {publicProfile!.city ? ` · ${publicProfile!.city}` : ''}
                </small>
              </div>
            </div>

            {(p.heading as string) && <p className="profile-sheet-heading">{String(p.heading)}</p>}
            {(p.bio as string) && <p className="profile-sheet-bio">{String(p.bio)}</p>}
            <SocialLinksRow links={detail?.socialLinks ?? []} />

            {detail?.tags.length ? (
              <div className="chip-row">
                {detail.tags.map((tag) => (
                  <span key={tag} className="chip selected">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            <ReportBlock reportedId={profileId} targetType="user" targetId={profileId} notify={notify} />
            {actionBar}
          </>
        )}
      </div>
    </>
  )
}
